import { isEmpty } from 'lodash';

import { connect } from 'react-redux';
import i18next from 'i18next';
import moment from 'moment-timezone';
import React, { Fragment, useState, useEffect } from 'react';

import { loading, loadingSuccess } from '../../../actions/loading';
import { openConfirmationModal } from '../../../actions/confirmationmodal';
import { showErrorMessage, showSuccessMessage } from '../../../actions/messageconfirmation';

import { event as eventService } from '../../../services/event';

import { Button, Dropdown, INPUT_WIDTH } from '../../../commons/utils/styledLibraryComponents';
import { SingleDatePicker } from '../../../commons/DatePickers/SingleDatePicker';
import ConfirmationModal from '../../../commons/DeepsightComponents/ConfirmationModal/ConfirmationModal';
import Footer from '../../../commons/Footer/Footer';
import Text, { ENUM_COLORS, ENUM_FONTS } from '../../../commons/Text';

import { Input } from '../../../lib/inpulse/Input';

import { QUARTER_HOURS_DROPDOWN_ITEMS } from '../../admin/utils/DropdownItems';

import { DEFAULT_TIMEZONE } from '@commons/utils/timezone';
import {
  List,
  Divider,
  Container,
  TitleContainer,
  DetailContainer,
  ButtonsContainer,
  ContentContainer,
} from './styledComponents';

const DEFAULT_EMPTY_EVENT = {
  endDate: null,
  startDate: null,
  name: null,
};

const getHourItems = (minTime) => {
  const items = [...QUARTER_HOURS_DROPDOWN_ITEMS, { id: '23:59', value: '23:59' }];

  if (!minTime) {
    return items;
  }

  const indexMinTime = items.findIndex((item) => item.value === minTime);

  if (indexMinTime == null) {
    return items;
  }

  items.splice(0, indexMinTime);

  return items;
};

const getTimeFromDateMoment = (date, timezone) => {
  if (!date) {
    return null;
  }

  const timezonedDate = moment.tz(date, timezone);

  const formattedHourMinute = timezonedDate.format('HH:mm', { trim: false });

  return { id: formattedHourMinute, value: formattedHourMinute };
};

const EventForm = (props) => {
  const {
    stores,
    storeId,
    closeForm,
    selectedEvent,
    loadStoreEvents,
    eventSubCategories,
    // Methods
    pageLoaded,
    pageLoading,
    showErrorMessage,
    showSuccessMessage,
    openConfirmationModal,
  } = props;

  const [formattedSubCategories, setFormattedSubCategories] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [event, setEvent] = useState({ ...DEFAULT_EMPTY_EVENT });

  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [selectedEventSubCategory, setSelectedEventSubCategory] = useState(null);

  const isEdition = !!selectedEvent;

  const close = () => {
    setEvent({ ...DEFAULT_EMPTY_EVENT });
    setSelectedSubCategory(null);
    setSelectedEventSubCategory(null);
    closeForm();
  };

  const isFormValid = () =>
    !isEmpty(selectedSubCategory) &&
    !isEmpty(selectedEventSubCategory) &&
    event.startDate &&
    event.endDate &&
    event.name;

  const handleSave = async () => {
    pageLoading();

    try {
      await eventService.patchEvent(
        isEdition ? selectedEvent.id : null,
        storeId,
        selectedSubCategory.id,
        moment(event.startDate).format(),
        moment(event.endDate).format(),
        event.name,
      );

      loadStoreEvents();

      showSuccessMessage(
        i18next.t(
          isEdition
            ? 'FORECAST.TURNOVER.EVENTS_EDITION_SUCCESS'
            : 'FORECAST.TURNOVER.EVENTS_ADDED_SUCCESSFULLY',
        ),
      );

      close();
    } catch (err) {
      showErrorMessage(
        i18next.t(
          isEdition
            ? 'FORECAST.TURNOVER.EVENTS_EDITION_ERROR'
            : 'FORECAST.TURNOVER.EVENTS_CREATION_ERROR',
        ),
      );
    } finally {
      pageLoaded();
    }
  };

  const handleDeleteEventConfirmation = () => {
    const params = {
      colorsheme: 'red',
      component: ConfirmationModal,
      title: i18next.t('FORECAST.TURNOVER.EVENT_DELETE_MODAL_TITLE'),
      text: i18next.t('FORECAST.TURNOVER.EVENT_DELETE_MODAL_CONTENT'),
      handleEventDeleteConfirmation: () => deleteEvent(selectedEvent.id),
    };

    openConfirmationModal(params);
  };

  const deleteEvent = async (eventId) => {
    pageLoading();

    try {
      await eventService.deleteEventById(eventId, storeId);

      loadStoreEvents();

      showSuccessMessage(i18next.t('FORECAST.TURNOVER.EVENTS_DELETION_SUCCESS'));

      close();
    } catch (err) {
      showErrorMessage(i18next.t('FORECAST.TURNOVER.EVENTS_DELETION_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  const setSelectedTime = (propertyKey, value) => {
    const clonedDate = event[propertyKey].clone();

    const [selectedHour, selectedMinute] = value.split(':');

    clonedDate.hour(selectedHour);
    clonedDate.minute(selectedMinute);

    updateEventForm(propertyKey, clonedDate);
  };

  const updateEventForm = (propertyKey, value) => {
    setEvent({ ...event, [propertyKey]: value });
  };

  const formatEventSubCategories = (eventSubCategories) =>
    eventSubCategories.map((subCategory) => {
      const { id, nameTranslationKey } = subCategory;

      return {
        id: id,
        value: i18next.t(nameTranslationKey),
      };
    });

  const renderCustomFields = () => (
    <Fragment>
      <DetailContainer>
        <Input
          label={`* ${i18next.t('FORECAST.TURNOVER.EVENTS_MODAL_FIELD_NAME')}`}
          name="name"
          value={event.name}
          width={INPUT_WIDTH.EXTRA_LARGE}
          isRequired
          textBig
          useParentHeight
          onChange={(event) => updateEventForm('name', event.target.value)}
        />
      </DetailContainer>

      <DetailContainer>
        <Text style={{ marginBottom: '8px' }}>
          <span style={{ color: '#FA393E', marginRight: '2px' }}>*</span>
          {i18next.t('FORECAST.TURNOVER.EVENTS_MODAL_FIELD_START_DATE')}
        </Text>
        <div style={{ display: 'flex', width: '100%', gap: '8px' }}>
          <SingleDatePicker
            customStyle={{ width: '220px' }}
            date={event.startDate}
            placeholder={i18next.t('GENERAL.DATEPICKER_PLACEHOLDER')}
            timezone={selectedStore ? selectedStore.timezone : DEFAULT_TIMEZONE}
            onDateChange={(value) =>
              updateEventForm('startDate', value.clone().hours(0).minutes(0))
            }
          />
          <Dropdown
            isDisabled={!event.startDate}
            items={getHourItems()}
            placeholder={i18next.t('ADMIN.TRANSACTIONS.COLUMN_NAME_HOUR')}
            selectedItem={getTimeFromDateMoment(
              event.startDate,
              selectedStore ? selectedStore.timezone : DEFAULT_TIMEZONE,
            )}
            width={'124px'}
            isLabelTextSmall
            isRequired
            onSelectionChange={(selectedItem) => setSelectedTime('startDate', selectedItem.value)}
          />
        </div>
      </DetailContainer>
      <DetailContainer>
        <Text style={{ marginBottom: '8px' }}>
          <span style={{ color: '#FA393E', marginRight: '2px' }}>*</span>
          {i18next.t('FORECAST.TURNOVER.EVENTS_MODAL_FIELD_END_DATE')}
        </Text>
        <div style={{ display: 'flex', width: '100%', gap: '8px' }}>
          <SingleDatePicker
            customStyle={{ width: '220px' }}
            date={event.endDate}
            disabled={!event.startDate}
            maxPastDate={event.startDate}
            placeholder={i18next.t('GENERAL.DATEPICKER_PLACEHOLDER')}
            timezone={selectedStore ? selectedStore.timezone : DEFAULT_TIMEZONE}
            onDateChange={(value) =>
              updateEventForm('endDate', value.clone().hours(23).minutes(59))
            }
          />
          <Dropdown
            isDisabled={!event.endDate}
            items={
              // Param necessary to handle unselectable time based on selected start date
              getHourItems(
                event.startDate &&
                  event.startDate.isSame(event.endDate, 'day') &&
                  event.startDate.format('HH:mm', { trim: false }),
              )
            }
            placeholder={i18next.t('ADMIN.TRANSACTIONS.COLUMN_NAME_HOUR')}
            selectedItem={getTimeFromDateMoment(
              event.endDate,
              selectedStore ? selectedStore.timezone : DEFAULT_TIMEZONE,
            )}
            width={'124px'}
            isLabelTextSmall
            isRequired
            onSelectionChange={(selectedItem) => setSelectedTime('endDate', selectedItem.value)}
          />
        </div>
      </DetailContainer>
      {isEdition && (
        <DetailContainer>
          <div
            style={{ display: 'flex', gap: '8px', cursor: 'pointer' }}
            onClick={handleDeleteEventConfirmation}
          >
            <img alt="cross-remove-reference" src="/images/inpulse/close-black-small.svg" />
            <Text color={ENUM_COLORS.DARKEST} font={ENUM_FONTS.TEXT_BIG_BOLD}>
              {i18next.t('FORECAST.TURNOVER.EVENTS_DELETE_ACTION')}
            </Text>
          </div>
        </DetailContainer>
      )}
    </Fragment>
  );
  useEffect(() => {
    if (!selectedEvent) {
      return;
    }

    // Additional safety check to ensure selectedEvent is a valid event object
    // and not a SyntheticBaseEvent or other unexpected object, as sent by the Drawer in the CustomCalendar via the close prop
    if (!selectedEvent.id || !selectedEvent.startDate || !selectedEvent.endDate) {
      setSelectedSubCategory(null);

      return;
    }

    setEvent({
      endDate: moment(selectedEvent.endDate),
      startDate: moment(selectedEvent.startDate),
      name: selectedEvent.name,
    });

    // The following has to be done so that the Dropdown component displays a translated value instead of the hardcoded french name
    const translatedSubCategory = {
      ...selectedEvent.eventSubCategory,
      name: i18next.t(selectedEvent?.eventSubCategory?.nameTranslationKey), // Legacy column name in DB
    };

    setSelectedSubCategory(translatedSubCategory);
  }, [selectedEvent]);

  // Get current store
  useEffect(() => {
    if (!storeId) {
      return;
    }

    const matchingStore = stores.find(({ id }) => storeId === id);

    if (!matchingStore) {
      return;
    }

    setSelectedStore(matchingStore);
  }, [storeId]);

  // Reset end date when start date has changed
  useEffect(() => {
    if (!event.startDate) {
      return;
    }

    updateEventForm('endDate', event.startDate.clone().hours(23).minutes(59));
  }, [event.startDate]);

  // Format & compute initial data
  useEffect(() => {
    const resultFormattedSubCategories = formatEventSubCategories(eventSubCategories);

    setFormattedSubCategories(resultFormattedSubCategories);
  }, [eventSubCategories]);

  // Get EventSubCategory from selected sub category
  useEffect(() => {
    if (!selectedSubCategory) {
      setSelectedEventSubCategory(null);

      return;
    }

    const currentSubCategory = eventSubCategories.find(({ id }) => id === selectedSubCategory.id);

    if (!currentSubCategory) {
      return;
    }

    setSelectedEventSubCategory(currentSubCategory);
  }, [selectedSubCategory]);

  return (
    <Container>
      <ContentContainer>
        <TitleContainer>
          {i18next.t(
            isEdition
              ? 'FORECAST.TURNOVER.EVENTS_MODAL_EDIT_THE_EVENT'
              : 'FORECAST.TURNOVER.EVENTS_MODAL_ADD_AN_EVENT',
          )}
        </TitleContainer>

        <Divider />

        <List>
          <DetailContainer>
            <Dropdown
              height={'fit-content'}
              items={formattedSubCategories}
              label={i18next.t('FORECAST.TURNOVER.EVENTS_MODAL_FIELD_CATEGORY')}
              selectedItem={selectedSubCategory}
              width={INPUT_WIDTH.EXTRA_LARGE}
              isRequired
              onSelectionChange={setSelectedSubCategory}
            />
          </DetailContainer>
          {renderCustomFields()}
        </List>
      </ContentContainer>

      <Divider />

      <Footer
        customStyle={{
          'box-shadow': 'none',
          'background-color': 'transparent',
          height: 'calc(100% - calc(100% - (64px + 24px)))',
          padding: '24px 0px',
        }}
      >
        <ButtonsContainer>
          <Button
            buttonCustomStyle={{ justifyContent: 'center', padding: '0px 32px' }}
            color={'inpulse-outline'}
            handleClick={close}
            icon={'/images/inpulse/close-black-small.svg'}
            label={i18next.t('GENERAL.LEAVE')}
            minWidth={168}
          />
          <Button
            buttonCustomStyle={{ justifyContent: 'center', padding: '0px 32px' }}
            color={'inpulse-default'}
            handleClick={handleSave}
            icon={'/images/inpulse/save-white-small.svg'}
            isDisabled={!isFormValid()}
            label={i18next.t('GENERAL.SAVE')}
            minWidth={168}
          />
        </ButtonsContainer>
      </Footer>
    </Container>
  );
};

const mapStateToProps = (state) => ({
  user: state.baseReducer.user,
  stores: state.baseReducer.activeStores,
});

const mapDispatchToProps = (dispatch) => ({
  pageLoading: () => {
    dispatch(loading());
  },
  pageLoaded: () => {
    dispatch(loadingSuccess());
  },
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
  showSuccessMessage: (message) => {
    dispatch(showSuccessMessage(message));
  },
  openConfirmationModal: (params) => {
    dispatch(openConfirmationModal(params));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(EventForm);
